// Product Control Plane — catalog_grid editor.
//
// Validation is inline and immediate, because the alternative is an operator
// discovering at publish time that row nine has an http:// url. A publish that
// fails is a good outcome; a publish that fails at the END of a careful review
// is a wasted review.
//
// The other thing this screen does that a plain CRUD table does not: it says
// which rows will NOT render, and why. `environment` and `minAppVersion` are
// one-word edits that delete a row from a population of devices without
// removing it from the table, and that is invisible unless something says so.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ConfirmDialog } from "../components/ConfirmDialog";
import {
  AuthorityNotice,
  GAOSAFE_APPROVAL_REASON,
  useControlPlaneWritesBlocked,
} from "../authority";
import { getDraft, resetDraft, saveDraft } from "../controlPlane/api";
import { ApiHttpError } from "../api/client";
import {
  COMPONENT_TYPE_CATALOG_GRID,
  decideItemVisibility,
  HIDDEN_REASON_TEXT,
  inRenderOrder,
  ITEM_ENVIRONMENTS,
  LANES,
  validateDocumentRows,
  withItems,
  type CatalogItem,
  type ControlPlaneDocument,
  type ItemEnvironment,
  type Lane,
} from "../controlPlane/contract";
import { laneHost, useControlPlaneTheme } from "../controlPlane/theme";
import {
  Btn,
  Card,
  CpHeader,
  Empty,
  ErrorNote,
  LaneChip,
  StatusChip,
} from "../controlPlane/ui";
import { tdStyle, thStyle } from "../controlPlane/styles";

function describeError(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

const cellInput: React.CSSProperties = { padding: "4px 6px", fontSize: 12 };

function blankItem(sortOrder: number): CatalogItem {
  return {
    id: "",
    title: "",
    description: null,
    category: null,
    url: "",
    environment: "production",
    enabled: true,
    sortOrder,
    iconUrl: null,
    requiresAuth: false,
    minAppVersion: null,
  };
}

export function ControlPlaneEditor() {
  const { productId = "" } = useParams();
  const navigate = useNavigate();
  const { themeProps, toggle, theme } = useControlPlaneTheme();

  const [doc, setDoc] = useState<ControlPlaneDocument | null>(null);
  const [componentId, setComponentId] = useState<string>("");
  const [items, setItems] = useState<CatalogItem[]>([]);
  // null until the server says. The editor must not assert a lane it has
  // not been told, for the same reason LaneChip renders UNKNOWN.
  const [lane, setLane] = useState<Lane | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [previewLane, setPreviewLane] = useState<Lane>("production");
  const [previewVersion, setPreviewVersion] = useState("");
  // The hash of the draft THIS operator loaded. It travels back with the save
  // so the server can refuse an edit made against a draft someone else has
  // since moved. `null` is a real value — no draft and no published revision —
  // and is different from "not loaded yet".
  const [loadedHash, setLoadedHash] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await getDraft(productId);
      setDoc(d.document);
      setLoadedHash(d.documentHash);
      // The catalog_grid this editor edits (a `local_ai_models` document has
      // its own editor, `LocalAIModelsEditor`).
      const first = d.document.surfaces
        .flatMap((sf) => sf.components)
        .find((c) => c.type === COMPONENT_TYPE_CATALOG_GRID);
      setComponentId(first?.id ?? "");
      setItems(inRenderOrder(first?.type === COMPONENT_TYPE_CATALOG_GRID ? first.items : []));
      setDirty(false);
    } catch (e) {
      setError(describeError(e));
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    void load();
  }, [load]);

  // The lane the SERVER is on, for the banner. The preview lane below is a
  // separate, operator-chosen thing and is never confused with it.
  useEffect(() => {
    let cancelled = false;
    import("../controlPlane/api").then(async (m) => {
      try {
        const r = await m.listProducts();
        if (!cancelled) {
          setLane(r.lane);
          setPreviewLane(r.lane);
        }
      } catch {
        /* the banner degrades to DEV; the server still decides the real lane */
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const issues = useMemo(() => {
    if (!doc) return [];
    return validateDocumentRows(withItems(doc, componentId, items));
  }, [doc, componentId, items]);

  const issuesByItem = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const i of issues) {
      const list = m.get(i.itemId) ?? [];
      list.push(`${String(i.field)}: ${i.message}`);
      m.set(i.itemId, list);
    }
    return m;
  }, [issues]);

  const previewVersionOrNull = previewVersion.trim() === "" ? null : previewVersion.trim();

  const hidden = useMemo(() => {
    const m = new Map<string, string>();
    for (const it of items) {
      const d = decideItemVisibility(it, previewLane, previewVersionOrNull);
      if (!d.visible) m.set(it.id, HIDDEN_REASON_TEXT[d.reason]);
    }
    return m;
  }, [items, previewLane, previewVersionOrNull]);

  function patch(index: number, next: Partial<CatalogItem>) {
    setItems((prev) => prev.map((it, i) => (i === index ? { ...it, ...next } : it)));
    setDirty(true);
  }

  function move(index: number, delta: number) {
    setItems((prev) => {
      const to = index + delta;
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [row] = next.splice(index, 1);
      next.splice(to, 0, row!);
      // sortOrder is REWRITTEN from array position on every move. Leaving the
      // stored numbers alone and relying on array order would publish a
      // document whose rendered order does not match what the operator sees.
      return next.map((it, i) => ({ ...it, sortOrder: i + 1 }));
    });
    setDirty(true);
  }

  function remove(index: number) {
    setItems((prev) =>
      prev.filter((_, i) => i !== index).map((it, i) => ({ ...it, sortOrder: i + 1 })),
    );
    setDirty(true);
  }

  function add() {
    setItems((prev) => [...prev, blankItem(prev.length + 1)]);
    setDirty(true);
  }

  async function save() {
    if (!doc) return;
    setSaving(true);
    setError(null);
    try {
      await saveDraft(productId, withItems(doc, componentId, items), loadedHash);
      setDirty(false);
      await load();
    } catch (e) {
      // 409 is not a failure to save; it is someone else having saved. Saying
      // "try again" would be wrong — the same stale hash would be refused
      // again — so the operator is told to reload and redo the edit.
      const conflict =
        e instanceof ApiHttpError && e.status === 409
          ? "Someone else saved this draft while you were editing. Reload to " +
            "see their version — your changes on this screen have not been saved."
          : null;
      setError(conflict ?? describeError(e));
    } finally {
      setSaving(false);
    }
  }

  async function discard() {
    setDiscardOpen(false);
    setSaving(true);
    setError(null);
    try {
      await resetDraft(productId);
      await load();
    } catch (e) {
      setError(describeError(e));
    } finally {
      setSaving(false);
    }
  }

  const blocked = issues.length > 0;
  const writesBlocked = useControlPlaneWritesBlocked();

  return (
    <div {...themeProps} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <CpHeader
        breadcrumb={
          <>
            <Link to="/control-plane">Control Plane</Link>
            {" / "}
            <Link to={`/control-plane/${productId}`}>{productId}</Link>
          </>
        }
        title={componentId || "catalog_grid"}
        subtitle="home.catalog · catalog_grid"
        actions={
          <>
            {/* The draft band. Present on every editing screen, unmissable. */}
            <StatusChip tone="warn">EDITING DRAFT — NOT VISIBLE TO ANYONE</StatusChip>
            <LaneChip lane={lane} host={laneHost(lane)} />
            <button type="button" onClick={toggle}>
              {theme === "light" ? "Dark" : "Light"}
            </button>
          </>
        }
      />

      {writesBlocked && <AuthorityNotice scope="Draft edits cannot be saved." />}

      {error !== null && <ErrorNote>{error}</ErrorNote>}

      <ConfirmDialog
        writesBlocked={writesBlocked}
        open={discardOpen}
        tone="danger"
        title="Discard draft changes"
        description="This replaces the draft with the live revision. Unpublished edits are not recoverable — no revision is written for a draft."
        rows={[
          { key: "Product", value: productId },
          { key: "Reverts to", value: "the current live revision" },
        ]}
        confirmLabel="Discard changes"
        busy={saving}
        onConfirm={() => void discard()}
        onCancel={() => setDiscardOpen(false)}
      />

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <Btn onClick={add}>+ Add row</Btn>
        <Btn onClick={() => void save()} disabled={writesBlocked || saving || !dirty || blocked}
             title={
               writesBlocked
                 ? GAOSAFE_APPROVAL_REASON
                 : blocked
                   ? "Fix the issues below before saving"
                   : undefined
             }>
          {saving ? "Saving…" : dirty ? "Save draft" : "Saved"}
        </Btn>
        {/* Confirmed, because it is destructive and irreversible.
            `resetDraft` throws away every unpublished edit an operator has
            made and replaces the draft with the live revision — and it was
            the ONE destructive call in the Control Plane that fired straight
            off a click, with no dialog, sitting immediately beside "Save
            draft". Publish, rollback and bootstrap all confirm; this did not. */}
        <Btn
          onClick={() => setDiscardOpen(true)}
          disabled={writesBlocked || saving || !dirty}
          title={writesBlocked ? GAOSAFE_APPROVAL_REASON : undefined}
        >
          Discard changes
        </Btn>
        <span style={{ flex: 1 }} />
        <Btn
          tone="primary"
          disabled={blocked || dirty}
          title={
            blocked
              ? "Fix the issues below first"
              : dirty
                ? "Save the draft first"
                : undefined
          }
          onClick={() => navigate(`/control-plane/${productId}/review`)}
        >
          Review &amp; publish…
        </Btn>
      </div>

      {blocked && (
        <Card accent="danger" title={`${issues.length} issue${issues.length === 1 ? "" : "s"} block publish`}>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {issues.map((i, n) => (
              <li key={`${i.itemId}-${String(i.field)}-${n}`}>
                <b>{i.itemId || "(new row)"}</b> · {String(i.field)} —{" "}
                <span style={{ color: "var(--danger)" }}>{i.message}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card
        title="Rows"
        note={`${items.length} · order is what devices render · sortOrder is rewritten on every move`}
        style={{ padding: 0 }}
      >
        {loading ? (
          <Empty>Loading…</Empty>
        ) : items.length === 0 ? (
          <Empty>No rows. Add one to begin.</Empty>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1040 }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: 64 }}>Order</th>
                  <th style={thStyle}>id</th>
                  <th style={thStyle}>Title</th>
                  <th style={thStyle}>URL</th>
                  <th style={thStyle}>Icon</th>
                  <th style={{ ...thStyle, width: 110 }}>Category</th>
                  <th style={{ ...thStyle, width: 116 }}>Environment</th>
                  <th style={{ ...thStyle, width: 96 }}>Min version</th>
                  <th style={{ ...thStyle, width: 68 }}>Enabled</th>
                  <th style={{ ...thStyle, width: 60 }}>Auth</th>
                  <th style={{ ...thStyle, width: 34 }} />
                </tr>
              </thead>
              <tbody>
                {items.map((it, index) => {
                  const rowIssues = issuesByItem.get(it.id) ?? [];
                  return (
                    <tr
                      key={`${it.id}-${index}`}
                      style={
                        rowIssues.length > 0
                          ? { background: "var(--danger-bg, #2a1416)" }
                          : undefined
                      }
                    >
                      <td style={tdStyle}>
                        <div style={{ display: "flex", gap: 2 }}>
                          <button
                            type="button"
                            aria-label={`Move ${it.id || "row"} up`}
                            disabled={index === 0}
                            onClick={() => move(index, -1)}
                            style={{ padding: "2px 6px" }}
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            aria-label={`Move ${it.id || "row"} down`}
                            disabled={index === items.length - 1}
                            onClick={() => move(index, 1)}
                            style={{ padding: "2px 6px" }}
                          >
                            ↓
                          </button>
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <input
                          value={it.id}
                          aria-label="id"
                          onChange={(e) => patch(index, { id: e.target.value })}
                          style={{ ...cellInput, width: 130 }}
                        />
                      </td>
                      <td style={tdStyle}>
                        <input
                          value={it.title}
                          aria-label="title"
                          onChange={(e) => patch(index, { title: e.target.value })}
                          style={{ ...cellInput, width: 150 }}
                        />
                      </td>
                      <td style={tdStyle}>
                        <input
                          value={it.url}
                          aria-label="url"
                          onChange={(e) => patch(index, { url: e.target.value })}
                          style={{ ...cellInput, width: 220 }}
                        />
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          {/* A monogram, not a broken-image box: an icon that
                              fails to load must never look like a failed row. */}
                          <span
                            aria-hidden="true"
                            style={{
                              width: 20,
                              height: 20,
                              borderRadius: 3,
                              border: "1px solid var(--border)",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 10,
                              color: "var(--fg-dim)",
                              flex: "0 0 auto",
                              overflow: "hidden",
                            }}
                          >
                            {it.iconUrl ? (
                              <img
                                src={it.iconUrl}
                                alt=""
                                width={20}
                                height={20}
                                style={{ objectFit: "cover" }}
                                onError={(e) => {
                                  (e.currentTarget as HTMLImageElement).style.display = "none";
                                }}
                              />
                            ) : (
                              (it.title || it.id || "?").slice(0, 1).toUpperCase()
                            )}
                          </span>
                          <input
                            value={it.iconUrl ?? ""}
                            aria-label="iconUrl"
                            placeholder="https://…"
                            onChange={(e) =>
                              patch(index, {
                                iconUrl: e.target.value === "" ? null : e.target.value,
                              })
                            }
                            style={{ ...cellInput, width: 150 }}
                          />
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <input
                          value={it.category ?? ""}
                          aria-label="category"
                          onChange={(e) =>
                            patch(index, {
                              category: e.target.value === "" ? null : e.target.value,
                            })
                          }
                          style={{ ...cellInput, width: 100 }}
                        />
                      </td>
                      <td style={tdStyle}>
                        <select
                          value={it.environment}
                          aria-label="environment"
                          onChange={(e) =>
                            patch(index, {
                              environment: e.target.value as ItemEnvironment,
                            })
                          }
                          style={{ ...cellInput, width: 106 }}
                        >
                          {ITEM_ENVIRONMENTS.map((v) => (
                            <option key={v} value={v}>
                              {v}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={tdStyle}>
                        <input
                          value={it.minAppVersion ?? ""}
                          aria-label="minAppVersion"
                          placeholder="1.4.0"
                          onChange={(e) =>
                            patch(index, {
                              minAppVersion: e.target.value === "" ? null : e.target.value,
                            })
                          }
                          style={{ ...cellInput, width: 86 }}
                        />
                      </td>
                      <td style={tdStyle}>
                        <label style={{ display: "inline-flex", gap: 5, alignItems: "center" }}>
                          <input
                            type="checkbox"
                            checked={it.enabled}
                            onChange={(e) => patch(index, { enabled: e.target.checked })}
                            style={{ width: "auto" }}
                          />
                          <span style={{ color: it.enabled ? "var(--ok)" : "var(--fg-dim)" }}>
                            {it.enabled ? "on" : "off"}
                          </span>
                        </label>
                      </td>
                      <td style={tdStyle}>
                        <input
                          type="checkbox"
                          aria-label="requiresAuth"
                          checked={it.requiresAuth}
                          onChange={(e) => patch(index, { requiresAuth: e.target.checked })}
                          style={{ width: "auto" }}
                        />
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        <button
                          type="button"
                          aria-label={`Remove ${it.id || "row"}`}
                          onClick={() => remove(index)}
                          style={{ padding: "2px 7px" }}
                        >
                          ×
                        </button>
                      </td>
                      {/* per-row messages live in their own row so the grid
                          stays readable at 11 columns */}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div style={{ padding: "10px 0 4px", display: "flex", flexDirection: "column", gap: 4 }}>
              {items.map((it, index) => {
                const rowIssues = issuesByItem.get(it.id) ?? [];
                const hiddenWhy = hidden.get(it.id);
                if (rowIssues.length === 0 && !hiddenWhy) return null;
                return (
                  <div key={`msg-${it.id}-${index}`} style={{ fontSize: 12 }}>
                    <b>{it.id || "(new row)"}</b>{" "}
                    {rowIssues.map((m) => (
                      <span key={m} style={{ color: "var(--danger)", marginRight: 10 }}>
                        {m}
                      </span>
                    ))}
                    {hiddenWhy && (
                      <span style={{ color: "var(--warn)" }}>
                        will not render in the preview below — {hiddenWhy}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>

      <Card
        title="Preview as"
        note="what a device would actually render — the visibility policy, applied"
      >
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--fg-dim)", marginBottom: 4 }}>
              Lane
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {LANES.map((l) => (
                <Btn
                  key={l}
                  tone={previewLane === l ? "primary" : "default"}
                  onClick={() => setPreviewLane(l)}
                >
                  {l === "production" ? "PRODUCT" : l.toUpperCase()}
                </Btn>
              ))}
            </div>
          </div>
          <div>
            <label
              htmlFor="cp-preview-version"
              style={{ display: "block", fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--fg-dim)", marginBottom: 4 }}
            >
              App version
            </label>
            <input
              id="cp-preview-version"
              value={previewVersion}
              placeholder="(none declared)"
              onChange={(e) => setPreviewVersion(e.target.value)}
              style={{ width: 150 }}
            />
          </div>
          <div style={{ color: "var(--fg-dim)", paddingBottom: 6 }}>
            {items.length - hidden.size} of {items.length} rows would render.
            {previewVersionOrNull === null && (
              <>
                {" "}
                A client that declares no version never sees a version-gated row
                — that is fail-closed, not a bug.
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
